import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import {CommonService, GetPageOfChatUsers, ChatState, ChatUser, ChatStateModel, ChatUserPage, ChatStore, ChatSupport} from 'huddyapi';
import { Subscription } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store, Select } from '@ngxs/store';
import { Observable } from 'rxjs';

interface SendMessage {
  messageType: 'SEND_MESSAGE';
  data: {
    chatRoomId: number;
    senderId: number;
    content: string;
    createdAt: number;
  }
}

interface CreateChatRoom {
  messageType: 'CREATE_CHAT_ROOM';
  data: {
    chatRoomId: number;
    senderId: number;
    senderEmail: string;
    senderUsername: string;
    senderTimezone: string;
    recipients: RecipientData[];
  };
}

interface RecipientData {
  recipientId: number;
  recipientEmail: string;
  recipientUsername: string;
  recipientTimezone: string;
}


interface ChatRoom {
  id: number;
  senderId: number;
  recipients: string;
}

interface Message {
  senderName: string;
  content: string;
  createdAt: number;
}

@Component({
  selector: 'chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit {
  readonly chatStore = inject(ChatStore);
  title = 'client';
  newMessage = '';
  messages: Message[] = [];
  userName: string = '';
  userEmail: string = '';
  userId: number | null = null;
  selectedRecipients: number[] = [];
  availablePatientUsers: ChatUser[] = [];
  availableProviderUsers: ChatUser[] = [];
  chatRooms: ChatRoom[] = [];
  selectedChatRoomId: number | null = null;
  sub: Subscription = new Subscription;
  socket!: WebSocket;
  userSubscription: any;
  user: any;
  chatSupport : ChatSupport ;


  private authToken = CommonService.jwt.substring(7).trim();

  constructor(private zone: NgZone, 
    private http: HttpClient,
    private store: Store,
    private commonService : CommonService) {
  }

  ngOnInit(): void {
    this.userSubscription = this.commonService.getUserSubject().subscribe((user: any) => {
      this.user = user || '';
    });
    this.chatSupport = this.chatStore.getCurrentChatSupport()  ;
    this.userName = this.getUserName();
    this.userId = this.user.id;
    this.userEmail = this.user.email;
    console.log("email: ", this.userEmail);
    this.messages = [];
    this.chatRooms = [];
    this.socket = new WebSocket(`wss://api-qa.huddy.io/chat?token=${encodeURIComponent(this.authToken)}`);
    //this.socket = new WebSocket(`ws://localhost:8080/chat?token=${encodeURIComponent(this.authToken)}`);
    this.socket.onopen = () => {
      this.getChatRooms();
    };

    this.socket.onmessage = event => {
      const parsedMessage = JSON.parse(event.data as string);
      this.zone.run(() => {
        switch (parsedMessage.messageType) {
          case 'SEND_MESSAGE':
            this.handleSendMessage(parsedMessage);
            break;
          case 'CREATE_CHAT_ROOM':
            this.handleCreateChatRoom(parsedMessage);
            break;
          default:
            console.warn('Unknown message type:', parsedMessage.messageType);
        }
      });
    };

    this.getAvailableUsers();
  }

  getAvailableUsers() {
    this.store.dispatch(new GetPageOfChatUsers(0, 10, '', '', '', 0))
      .subscribe(() => {
        console.log("Chat State:", this.chatSupport);
        this.availablePatientUsers = this.chatSupport.patients.content;
        this.availableProviderUsers = this.chatSupport.providerUsers;
      });
  }

  private handleSendMessage(parsedMessage: SendMessage): void {
    if (parsedMessage.data.chatRoomId === this.selectedChatRoomId) {
      if (parsedMessage.data.content) {
        const senderName = this.getUserName();
        const existingMessage = this.messages.find(message => 
          message.content === parsedMessage.data.content &&
          message.senderName === senderName &&
          message.createdAt === parsedMessage.data.createdAt
        );
  
        if (!existingMessage) {
          this.addMessage({
            senderName: senderName,
            content: parsedMessage.data.content,
            createdAt: parsedMessage.data.createdAt
          });
        }
      }
    }
  }
  
  private handleCreateChatRoom(parsedMessage: CreateChatRoom) {
    const existingChatRoom = this.chatRooms.find(room => room.id === parsedMessage.data.chatRoomId);
    const isUserInChatRoomAsSender = parsedMessage.data.senderId === this.userId;
    const isUserInChatRoomAsRecipient = parsedMessage.data.recipients.some(recipient => recipient.recipientId === this.userId);

    let displayName: string;
    if (isUserInChatRoomAsSender) {
        displayName = `${parsedMessage.data.recipients.map(recipient => recipient.recipientUsername).join(', ')}`;
    } else if (isUserInChatRoomAsRecipient) {
        const otherRecipients = parsedMessage.data.recipients
            .filter(recipient => recipient.recipientId !== this.userId)
            .map(recipient => recipient.recipientUsername)
            .join(', ');

        displayName = `${parsedMessage.data.senderUsername}${otherRecipients ? `, ${otherRecipients}` : ''}`;
    } else {
        return;
    }

    if (existingChatRoom) {
        existingChatRoom.recipients = displayName;
    } else {
        const chatRoom = {
            id: parsedMessage.data.chatRoomId,
            senderId: parsedMessage.data.senderId,
            recipients: displayName
        };
        this.chatRooms.push(chatRoom);
    }
  }

  addMessage(msg: any) {
    this.messages = [...this.messages, msg];
    //console.log("messages::" + this.messages);
  }

  ngOnDestroy(): void {
    this.sub && this.sub.unsubscribe();
    if (this.socket) {
      this.socket.close();
    }
    this.messages = [];
    this.chatRooms = [];
  }

  sendMessage() {
    if (this.selectedChatRoomId === null) {
      alert('Please select a chat room first.');
      return;
    }

    console.log("sending message:" + this.newMessage);
    const chatMessage = {
      messageType: 'SEND_MESSAGE',
      data: {
        chatRoomId: this.selectedChatRoomId,
        senderId: this.userId,
        content: this.newMessage,
        createdAt: Date.now()
      }
    };

    this.socket.send(JSON.stringify(chatMessage));
    this.newMessage = '';
  }

  createChatRoom() {
    if (this.selectedRecipients.length > 0 && this.userId !== null) {
      const selectedRecipientDetails = this.selectedRecipients.map(id => {
        let user = this.availablePatientUsers.find(user => user.id === id);

        if (!user) {
          user = this.availableProviderUsers.find(user => user.id === id);
        }
        return user ? {
          recipientId: user.id,
          recipientEmail: user.username || "N/A",
          recipientUsername: user.name,
          recipientTimezone: 'America/New_York' // TODO Replace with actual timezone
        } : null;
      }).filter(recipient => recipient !== null) as any;
  
      const sortedSelectedRecipients = selectedRecipientDetails
      .map((r: any) => r.recipientUsername)
      .sort();

      const chatRoomExists = this.chatRooms.some(room => {
      const recipientNames = room.recipients.split(',').map(name => name.trim()).sort();

      return room.senderId === this.userId &&
        recipientNames.length === sortedSelectedRecipients.length &&
        recipientNames.every((name, index) => name === sortedSelectedRecipients[index]);
    });

  
      if (chatRoomExists) {
        alert('This chat already exists.');
        return;
      }
  
      const createChatRoomMessage = {
        messageType: 'CREATE_CHAT_ROOM',
        data: {
          senderId: this.userId,
          senderEmail: this.userEmail,
          senderUsername: this.userName,
          senderTimezone: 'America/New_York', // TODO Replace with actual timezone
          createdAt: Date.now(),
          recipients: selectedRecipientDetails
        }
      };
  
      this.socket.send(JSON.stringify(createChatRoomMessage));
      this.selectedRecipients = [];
    } else {
      alert('Please select at least one recipient.');
    }
  }
  

  selectChatRoom(chatRoomId: number) {
    this.selectedChatRoomId = chatRoomId;
    this.messages = [];
    const getMessagesMessage = {
      messageType: 'GET_MESSAGES',
      data: {
        chatRoomId: this.selectedChatRoomId
      }
    };
    this.socket.send(JSON.stringify(getMessagesMessage));
  }

  openCreateChatRoomDialog() {
    this.createChatRoom();
    this.selectedRecipients = [];
  }

  onRecipientChange(event: any) {
    const userId = Number(event.target.value);
    if (event.target.checked) {
      this.selectedRecipients.push(userId);
    } else {
      this.selectedRecipients = this.selectedRecipients.filter(id => id !== userId);
    }
  }

  private getChatRooms() {
    if (this.userId) {
      const getChatRoomsMessage = {
        messageType: 'GET_CHAT_ROOMS',
        data: {
          userId: this.userId
        }
      };
      console.log(JSON.stringify(getChatRoomsMessage));
      this.socket.send(JSON.stringify(getChatRoomsMessage));
    }

  }

  isRecipientSelected(userId: number): boolean {
    return this.selectedRecipients.includes(userId);
  }

  getUserName() : string {

    let ret = '';
    if (this.user)
     ret = this.getName(this.user);
    return ret;
  }

  getName(user: any) {
    if(!user)
      return;
    if(user.firstName && user.lastName)
      return user.firstName + ' ' + user.lastName;
    return this.getShortName(user);
  }

  getShortName(user: any) {
    if(!user)
      return;
    if(user.firstName)
      return user.firstName;
    if(user.lastName)
      return user.lastName;
    if (user.email) {
        let index = user.email.indexOf('@');
        return index < 0 ? user.email : user.email.substring(0, index);
    }
    return "";
  }

  generateZoomMeeting() {
    var zoomApiUrl = "https://api-qa.huddy.io/api/zoom/create-meeting";
    //var zoomApiUrl = 'http://localhost:8080/api/zoom/create-meeting';
  
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authToken}`
    });
  
    this.http.get(zoomApiUrl, { headers })
      .subscribe((response: any) => {
        if (response && response.join_url) {
          this.sendMessageWithZoomUrl(response.join_url);
        }
      }, error => {
        console.error('Error creating Zoom meeting:', error);
      });
  }

  sendMessageWithZoomUrl(zoomUrl: string) {
    if (this.selectedChatRoomId === null) {
      alert('Please select a chat room first.');
      return;
    }
    const chatMessage = {
      messageType: 'SEND_MESSAGE',
      data: {
        chatRoomId: this.selectedChatRoomId,
        senderId: this.userId,
        content: `Join the Zoom meeting here: <a href="${zoomUrl}" target="_blank" rel="noopener noreferrer">Zoom meeting</a> (${zoomUrl})`,
        createdAt: Date.now()
      }
    };

    this.socket.send(JSON.stringify(chatMessage));
  }

  createZoomMeeting() {
    this.generateZoomMeeting();
  }



}